import React, { useState, useMemo, useEffect } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import underwritingApi from './api/underwritingApi';
import getTenantIdFromURL from './utils/getTenantIdFromURL';
import { useSelector } from 'react-redux';
import { Box, TextField, Button, Typography, Modal, Checkbox } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { red } from '@mui/material/colors';

// Custom checkbox component that will re-render when props change
const RiskCheckbox = ({ row, tenantId, onUpdate }) => {
  const [isChecked, setIsChecked] = useState(row.inRisk);
  
  // Update local state when row.inRisk changes
  useEffect(() => {
    setIsChecked(row.inRisk);
  }, [row.inRisk]);

  const handleChange = async (event) => {
    const newValue = event.target.checked;
    setIsChecked(newValue); // Update local state immediately for UI responsiveness
    
    try {
      await underwritingApi.updateGeneralQuestion(tenantId, row.id, { inRisk: newValue });
      onUpdate(row.id, newValue); // Notify parent component
    } catch (error) {
      console.error("Error updating question:", error);
      setIsChecked(!newValue); // Revert on error
    }
  };

  return (
    <Checkbox
      checked={isChecked}
      onChange={handleChange}
      sx={{ color: red[800], '&.Mui-checked': { color: red[600] } }}
    />
  );
};

export default function QuestionsPage({}) {
  const [rowData, setRowData] = useState([]);
  const [tenantId, setTenantId] = useState(undefined);
  const isAdmin = useSelector(state => state.userInfo.isAdmin);
  const URLTenant = getTenantIdFromURL();
  const currentTenant = useSelector(state => state.userInfo.tenantId);
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [textFieldValue, setTextFieldValue] = useState('');

  useEffect(() => {
    const selectTenantId = () => {
      if (isAdmin) setTenantId(URLTenant);
      else setTenantId(currentTenant);
    };
    selectTenantId();
  }, [isAdmin, URLTenant, currentTenant]);

  useEffect(() => {
    const questionsLoader = async () => {
      if (tenantId) {
        const result = await underwritingApi.getGeneralQuestions(tenantId);
        const questions = result.data || [];
        questions.sort((a, b) => a.position - b.position);
        setRowData(questions);
      }
    };
    questionsLoader();
  }, [tenantId]);

  const handleAddQuestionClick = async () => {
    if (rowData.length >= 100) {
      alert("You cannot add more than 100 questions.");
      return;
    }
    try {
      const lastPosition = rowData.length ? Math.max(...rowData.map(q => q.position)) : 0;
      const response = await underwritingApi.createGeneralQuestion(tenantId, textFieldValue);
      const question = response.data;
      setTextFieldValue("");
      setRowData([...rowData, { ...question, position: lastPosition + 1 }]);
    } catch (error) {
      console.error("Error adding question:", error);
    }
  };

  const handleTextFieldChange = (event) => {
    setTextFieldValue(event.target.value);
  };

  const handleDeleteQuestion = async (data) => {
    try {
      await underwritingApi.deleteGeneralQuestion(tenantId, data.id);
      const updatedData = rowData.filter((item) => item.id !== data.id);
      setRowData(updatedData);
    } catch (error) {
      console.error("Error deleting question:", error);
    }
  };

  const handleRowOrderChange = async ({ rowIds, oldIndex, targetIndex }) => {
    try {
      const questions = [...rowData];
      const [removed] = questions.splice(oldIndex, 1);
      questions.splice(targetIndex, 0, removed);

      // Update positions locally
      for (let i = 0; i < questions.length; i++) {
        questions[i].position = i + 1;
      }

      // Send the questionIds in the new order to the backend
      const questionIds = questions.map(q => q.id);
      await underwritingApi.reorderGeneralQuestions(tenantId, questionIds);

      setRowData(questions);
    } catch (error) {
      console.error("Error updating positions on row drag:", error);
    }
  };

  const handleCheckboxUpdate = (questionId, newInRiskValue) => {
    setRowData(prevData => 
      prevData.map(item => 
        item.id === questionId 
          ? { ...item, inRisk: newInRiskValue }
          : item
      )
    );
  };

  const columns = [
    {
      field: 'inRisk',
      headerName: 'In risk?',
      renderCell: (params) => (
        <RiskCheckbox 
          row={params.row} 
          tenantId={tenantId} 
          onUpdate={handleCheckboxUpdate}
        />
      ),
      width: 130
    },
    { field: 'question', headerName: 'Question', width: 750 },
    {
      field: 'actions',
      headerName: '',
      renderCell: (params) => (
        <button onClick={() => handleDeleteQuestion(params.row)}>Delete <DeleteForeverIcon/></button>
      ),
      width: 100
    }
  ];

  return (
    <div style={{display:"flex",justifyContent:'center',alignItems:'center',flexDirection:'column'}}>
      <Typography variant="h6" style={{ margin: '20px 0' }}>
        Total Questions: {rowData.length} / 100
      </Typography>
      <Box>
        <TextField
          style={{ width: 500 }}
          label="Insert a new question"
          variant="standard"
          value={textFieldValue}
          onChange={handleTextFieldChange}
        />
        <Button onClick={handleAddQuestionClick}>Add question</Button>
        {/* <Button
          onClick={async () => {
            setDisabled(true);
            await API.graphql(graphqlOperation(triggerQuestionsAgent, { tenantId }));
            setOpen(true);
            setDisabled(false);
          }}
          disabled={disabled}
        >
          Trigger questions agent
        </Button> */}
      </Box>
      <div style={{ height: 800, width: '80%' }}>
        <DataGridPro
          rows={rowData}
          columns={columns}
          getRowId={(row) => row.id}
          rowReordering
          onRowOrderChange={handleRowOrderChange}
        />
      </div>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4
        }}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            Agent initiated
          </Typography>
          <Typography id="modal-modal-description" sx={{ mt: 2 }}>
            All configured questions are being answered, visit the company page for answers
          </Typography>
        </Box>
      </Modal>
    </div>
  )
}
